import React, { useState, useEffect, useRef, useMemo } from 'react';
import { CheckCircle, Circle } from 'lucide-react';
import celebrateCompletion from '../utils/confetti';
import CompletionToast from './CompletionToast';
import debugLogger from '../utils/debug';

const ProgressTracker = ({ 
  sections, 
  currentSection, 
  responses = {},
  features = [],
  onSectionChange,
  viewMode,
  organization,
  dependencies = {},  // Change to dependencies
  dependents = {}     // Add dependents
}) => {
  const previousProgressRef = useRef({});
  const previousResponsesRef = useRef(null);
  const [showToast, setShowToast] = useState(false);
  const [completedSectionName, setCompletedSectionName] = useState('');
  const isInitializedRef = useRef(false);
  const hasReceivedFirstResponsesRef = useRef(false);

  // Calculate section progress with improved dependency handling
  const sectionProgress = useMemo(() => {
    debugLogger.debug('PROGRESS', 'Starting sectionProgress calculation', {
      sections,
      features,
      responses: Object.values(responses),
      dependencies,
      dependents
    });
  
    return sections.map((section) => {
      const sectionFeatures = features.filter((f) => f.section === section.name);
      debugLogger.debug('PROGRESS', `Processing section ${section.name}`, {
        featureCount: sectionFeatures.length,
        features: sectionFeatures
      });
      
      const completedFeatures = sectionFeatures.reduce((acc, feature) => {
        // Get all responses for this feature, ensuring we filter out undefined/null values
        const featureResponses = Object.values(responses || {})
          .filter(Boolean)  // Filter out null/undefined responses
          .filter(r => 
            r.feature_reference === feature.reference &&
            r.organization_id === organization?.id
          );
  
        // Check for user responses first
        const hasUserResponse = featureResponses.some(r => r.response_value && !r.is_system_dependency);
        if (hasUserResponse) {
          acc.userResponses.push(feature.reference);
        }

        // Check for actual system dependency responses (not features that HAVE dependencies)
        const hasSystemDependencyResponse = featureResponses.some(r => r.is_system_dependency);
        if (hasSystemDependencyResponse) {
          acc.systemDependencies.push(feature.reference);
        }
  
        return acc;
      }, { userResponses: [], systemDependencies: [] });
  
      const total = sectionFeatures.length;
      const userResponseCount = completedFeatures.userResponses.length;
      const systemDependencyCount = completedFeatures.systemDependencies.length;
      const totalCompleted = userResponseCount + systemDependencyCount;
  
      return {
        ...section,
        total,
        userResponses: userResponseCount,
        systemRequired: systemDependencyCount,
        completed: totalCompleted,
        percentage: Math.round((totalCompleted / total) * 100),
        userResponsePercentage: Math.round((userResponseCount / total) * 100),
        systemDependencyPercentage: Math.round((systemDependencyCount / total) * 100)
      };
    });
  }, [sections, features, responses, organization?.id, dependencies]);

  // Check for newly completed sections
  useEffect(() => {
    // Skip if this is the first load (previousResponses is null)
    if (!previousResponsesRef.current) {
      previousResponsesRef.current = responses;
      sectionProgress.forEach(section => {
        previousProgressRef.current[section.id] = section.percentage;
      });
      return;
    }

    // Check if responses have actually changed
    const hasResponsesChanged = JSON.stringify(previousResponsesRef.current) !== JSON.stringify(responses);
    previousResponsesRef.current = responses;

    if (!hasResponsesChanged) {
      return; // Skip if no actual changes to responses
    }

    // Only check for completion after a real response change
    sectionProgress.forEach(section => {
      const prevPercentage = previousProgressRef.current[section.id] || 0;
      const currentPercentage = section.percentage;

      if (prevPercentage !== 100 && currentPercentage === 100 && section.total > 0) {
        celebrateCompletion();
        setCompletedSectionName(section.name);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 5000);
      }

      previousProgressRef.current[section.id] = currentPercentage;
    });
  }, [sectionProgress, responses]);

  // Overall progress calculation
  const { totalAnswered, totalSystemRequired, totalFeatures, overallProgress } = useMemo(() => {
    debugLogger.debug('PROGRESS', 'Starting overall progress calculation');
    
    // Safely get responses list, filtering out undefined/null values
    const responsesList = Object.values(responses || {}).filter(Boolean);
    debugLogger.debug('PROGRESS', 'All responses', responsesList);
    
    // Count user responses
    const userResponses = responsesList.filter(
      r => r.organization_id === organization?.id && 
           r.response_value && 
           !r.is_system_dependency
    );
    debugLogger.debug('PROGRESS', 'User responses', userResponses);

    // Count actual system dependency responses (not features that HAVE dependencies)
    const systemDependencyResponses = responsesList.filter(
      r => r.organization_id === organization?.id && 
           r.is_system_dependency
    );
    
    const systemDependencyCounts = systemDependencyResponses.map(r => r.feature_reference);

    debugLogger.debug('PROGRESS', 'System dependency responses found', systemDependencyResponses);
    debugLogger.debug('PROGRESS', 'System dependency feature references', systemDependencyCounts);

    const total = features.length || 0;
    const answered = userResponses.length || 0;
    const systemRequired = systemDependencyCounts.length || 0;
    const totalCompleted = answered + systemRequired;

    debugLogger.debug('PROGRESS', 'Final progress counts', {
      total,
      answered,
      systemRequired,
      totalCompleted,
      percentage: total > 0 ? Math.round((totalCompleted / total) * 100) : 0
    });
    
    return {
      totalAnswered: answered,
      totalSystemRequired: systemRequired,
      totalFeatures: total,
      overallProgress: total > 0 ? Math.round((totalCompleted / total) * 100) : 0
    };
  }, [responses, features, organization?.id, dependencies]);

  return (
      <div 
        className="mb-8 bg-gray-50 overflow-y-auto custom-scrollbar"
        style={{ maxHeight: 'calc(100vh - 200px)' }}
      >
        <style>
          {`
            .custom-scrollbar::-webkit-scrollbar {
              width: 8px;
            }
    
            .custom-scrollbar::-webkit-scrollbar-track {
              background: #f1f1f1;
              border-radius: 4px;
            }
    
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #d1d5db;
              border-radius: 4px;
            }
    
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: #9ca3af;
            }
    
            .custom-scrollbar {
              scrollbar-width: thin;
              scrollbar-color: #d1d5db #f1f1f1;
            }
          `}
        </style>
      {/* Overall Progress */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Overall Progress</h3>
          <div>
            <span className="text-sm font-medium text-gray-600">
              {totalAnswered + totalSystemRequired}/{totalFeatures} ({overallProgress}% Complete)
            </span>
            <span className="ml-2 text-sm font-medium text-gray-600">
              <span className="text-green-600">{totalAnswered} Answered</span>
              {totalSystemRequired > 0 && (
                <span className="ml-1 text-blue-600">
                  • {totalSystemRequired} Auto-Required
                </span>
              )}
            </span>
          </div>
        </div>
        <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          {/* User responses - Green */}
          {totalAnswered > 0 && (
            <div 
              className="absolute h-full bg-green-600 transition-all duration-500"
              style={{ width: `${(totalAnswered / totalFeatures) * 100}%` }}
            />
          )}
          {/* Auto-required features - Blue */}
          {totalSystemRequired > 0 && (
            <div 
              className="absolute h-full bg-blue-600 transition-all duration-500"
              style={{ 
                width: `${(totalSystemRequired / totalFeatures) * 100}%`,
                left: `${(totalAnswered / totalFeatures) * 100}%`
              }}
            />
          )}
        </div>
      </div>

      {/* Section Navigation */}
      <div className="space-y-4">
        {sectionProgress.map((section) => (
          <button
            key={section.id}
            onClick={() => viewMode === 'sections' && onSectionChange(section.id)}
            className={`w-full flex items-center gap-4 p-4 rounded-lg transition-colors
                ${currentSection === section.id && viewMode === 'sections'
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-white hover:bg-gray-50'
                } border
                ${viewMode === 'all' ? 'cursor-not-allowed' : ''}`}
            disabled={viewMode === 'all'}
          >
            {section.percentage === 100 
              ? <CheckCircle className="w-5 h-5 text-green-600" />
              : <Circle className="w-5 h-5 text-gray-400" />
            }
            
            <div className="flex-1">
              <div className="flex justify-between items-center mb-1">
                <span className="font-medium text-gray-900">{section.name}</span>
                <div className="text-sm">
                  <span className="text-gray-600">
                    {section.completed}/{section.total} Complete
                  </span>
                  <span className="ml-2 text-green-600">
                    {section.userResponses} Answered
                  </span>
                  {section.systemRequired > 0 && (
                    <span className="ml-2 text-blue-600">
                      • {section.systemRequired} Auto-Required
                    </span>
                  )}
                </div>
              </div>
              
              <div className="relative w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                {/* User responses - Green */}
                {section.userResponses > 0 && (
                  <div 
                    className="absolute h-full bg-green-600 transition-all duration-500"
                    style={{ width: `${(section.userResponses / section.total) * 100}%` }}
                  />
                )}
                {/* Auto-required features - Blue */}
                {section.systemRequired > 0 && (
                  <div 
                    className="absolute h-full bg-blue-600 transition-all duration-500"
                    style={{ 
                      width: `${(section.systemRequired / section.total) * 100}%`,
                      left: `${(section.userResponses / section.total) * 100}%`
                    }}
                  />
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Toast */}
      {showToast && <CompletionToast sectionName={completedSectionName} />}
    </div>
  );
};

export default ProgressTracker;